import {
  HttpErrorResponse,
  HttpEvent,
  HttpHeaders,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { inject, Injectable, Provider } from '@angular/core';
import { delay, from, map, mergeMap, Observable, of, throwError } from 'rxjs';
import {
  AbstractSolHttpMock,
  AbstractSolHttpResponseTransformMock,
  SOL_HTTP_MOCKS,
  SolHttpMockConstructor,
  SolHttpMockResult,
} from './sol-http-mock';

@Injectable()
export class SolHttpMockInterceptor implements HttpInterceptor {
  private readonly mocks: AbstractSolHttpMock<unknown>[];

  constructor() {
    this.mocks = (inject(SOL_HTTP_MOCKS, { optional: true }) ?? [])
      .filter(Boolean)
      .map((mock: SolHttpMockConstructor | AbstractSolHttpMock<unknown>) =>
        this.instantiateMock(mock as SolHttpMockConstructor | AbstractSolHttpMock<unknown>),
      );
  }

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.mocks.length === 0) {
      return next.handle(request);
    }

    const mock = this.findMock(request);
    if (!mock) {
      return next.handle(request);
    }

    if (mock instanceof AbstractSolHttpResponseTransformMock) {
      return this.transformResponse(mock, request, next);
    }

    return from(Promise.resolve(mock.buildResponse(request))).pipe(
      mergeMap(result => this.toHttpEvent(result, request)),
    );
  }

  private findMock(request: HttpRequest<unknown>): AbstractSolHttpMock<unknown> | undefined {
    const matches = this.mocks.filter(mock => mock.matches(request));
    return matches.find(mock => !(mock instanceof AbstractSolHttpResponseTransformMock)) ?? matches[0];
  }

  private transformResponse(
    mock: AbstractSolHttpResponseTransformMock,
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next
      .handle(request)
      .pipe(
        map(event =>
          event instanceof HttpResponse ? event.clone({ body: mock.transform(event.body, request) }) : event,
        ),
      );
  }

  private instantiateMock(mock: SolHttpMockConstructor | AbstractSolHttpMock<unknown>): AbstractSolHttpMock<unknown> {
    if (mock instanceof AbstractSolHttpMock) {
      return mock;
    }
    return new mock();
  }

  private toHttpEvent(result: SolHttpMockResult, request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    const delayMs = result.delayMs ?? 0;
    const headers = new HttpHeaders(result.headers);

    if (result.error !== undefined) {
      const errorResponse = new HttpErrorResponse({
        status: result.status ?? 500,
        statusText: result.statusText ?? '',
        error: result.error,
        headers,
        url: result.url ?? request.url,
      });
      const error$ = throwError(() => errorResponse);
      return delayMs > 0 ? error$.pipe(delay(delayMs)) : error$;
    }

    const response = new HttpResponse({
      status: result.status ?? 200,
      body: result.body,
      headers,
      url: request.url,
    });
    const response$ = of(response);
    return delayMs > 0 ? response$.pipe(delay(delayMs)) : response$;
  }
}

export function provideSolHttpMockInterceptor(): Provider {
  return {
    provide: HTTP_INTERCEPTORS,
    useClass: SolHttpMockInterceptor,
    multi: true,
  };
}
